import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { Kiosk } from 'modules/kiosk/types/Kiosk';

import { useGetKioskQuery } from 'modules/kiosk/service';

import { getMFAError } from 'modules/auth/selectors';
import { getKioskId, getKioskImages } from 'modules/dealers/selectors';

type KioskLocationConfig = {
  title: React.ReactNode;
  description: React.ReactNode;
  image: Partial<HTMLImageElement>;
};

const DEFAULT_CONFIG: Omit<KioskLocationConfig, 'image'> = {
  title: <FormattedMessage id="kioskLocation.title" defaultMessage="Kiosk location" />,
  description: (
    <FormattedMessage
      id="kioskLocation.description"
      defaultMessage="Please present yourself in front of the kiosk with the following name before going to the next step."
    />
  ),
};

const ERROR_CONFIG: Omit<KioskLocationConfig, 'image'> = {
  title: <FormattedMessage id="wrongKiosk.title" defaultMessage="Change kiosk" />,
  description: (
    <FormattedMessage
      id="wrongKiosk.partteam.description"
      defaultMessage="Your keys are prepared in another kiosk, please start over on the kiosk with the following name"
    />
  ),
};

const useKioskLocationConfig = (): KioskLocationConfig & Kiosk => {
  const error = useSelector(getMFAError);
  const kioskId = useSelector(getKioskId);
  const kioskImages = useSelector(getKioskImages);

  const hasError = Boolean(error);
  const { data } = useGetKioskQuery(
    { kioskId },
    { skip: hasError || Boolean(kioskImages) || !kioskId },
  );

  const images = React.useMemo(
    () => error?.kioskImages ?? kioskImages ?? data?.images,
    [error, kioskImages, data],
  );

  return React.useMemo(() => ({
    ...data,
    ...(hasError ? ERROR_CONFIG : DEFAULT_CONFIG),
    image: {
      alt: 'Kiosk image',
      src: images?.image,
    },
  }), [hasError, data, images]);
};

export default useKioskLocationConfig;
